import { Board } from "./black_hole_game";

// Drives the Go (WASM) perfect solver, which runs in a Web Worker so the UI
// stays responsive. Mirrors the lazy-singleton shape of modelService's
// getModelSession: a worker is created and initialized once per board size on
// first use (5-row and 6-row need different WASM binaries).

const base = import.meta.env.BASE_URL.endsWith("/")
    ? import.meta.env.BASE_URL
    : `${import.meta.env.BASE_URL}/`;

interface MoveScore {
    move: number;
    score: number;
}
interface ResultMessage {
    type: "result";
    id: number;
    move: number;
    score?: number;
    moves?: MoveScore[];
    fromBook?: boolean;
    // Time-limit outcome flags from a live solve (absent for book hits/evaluate):
    // limitAborted = hard limitSeconds timeout, score may be suboptimal;
    // earlyCutoff = stopped on a W/D/L cutoff, result is trustworthy.
    limitAborted?: boolean;
    earlyCutoff?: boolean;
    // MCTS: whether the search proved the position exactly (via endgame solver).
    proven?: boolean;
    error?: string;
}

/** Tuning knobs for the MCTS+solver opponent (see pkg/blackhole/mcts.go). */
export interface MctsOptions {
    /** Simulation budget per move (higher = stronger + slower). */
    sims?: number;
    /** Wall-clock cap per move in ms; bounds mid-game frontier-solve cost. */
    maxMillis?: number;
    /** Solve exactly once empties <= k. */
    k?: number;
    exploration?: number;
    scale?: number;
    winloss?: boolean;
    /** Fixed seed for reproducible play; omit for varied (non-repetitive) play. */
    seed?: number;
    /** Which embedded net guides search: the book-distilled net (default) or AlphaHole, trained by self-play alone. */
    model?: MctsModel;
}

export type MctsModel = "aznet" | "alphahole";
interface ReadyMessage {
    type: "ready";
    error?: string;
}
type WorkerMessage = ResultMessage | ReadyMessage;

export type SolverMoveSource = "book" | "minmax" | "mcts";

export interface BookDepth {
    minPly: number;
    /** Deepest ply with any book coverage (may be partial at this ply). */
    maxPly: number;
    /** Deepest ply the book covers completely; <= maxPly when the top ply is partial. */
    completeMaxPly: number;
}

export interface SolverMoveResult {
    move: [number, number];
    source: SolverMoveSource;
    score?: number;
    /** Hard limitSeconds timeout fired mid-search; score may be suboptimal. */
    limitAborted?: boolean;
    /** Search stopped on a W/D/L cutoff; the move is trustworthy. */
    earlyCutoff?: boolean;
}

export interface SolverLimits {
    winSeconds?: number;
    drawSeconds?: number;
    limitSeconds?: number;
}

interface SolverHandle {
    worker: Worker;
    ready: Promise<void>;
    disposed: boolean;
    dispose: () => void;
}

// One solver worker per board size; each loads its own WASM binary.
const handles = new Map<number, SolverHandle>();
let nextRequestId = 0;
const pending = new Map<number, { worker: Worker; resolve: (result: ResultMessage) => void }>();
let solver6BookDepth: Promise<BookDepth | null> | null = null;

function getHandle(rows: number, registry = handles): SolverHandle {
    const existing = registry.get(rows);
    if (existing) {
        return existing;
    }

    const worker = new Worker(new URL("./solver.worker.ts", import.meta.url), { type: "module" });

    let resolveReady!: () => void;
    let rejectReady!: (err: Error) => void;
    const ready = new Promise<void>((resolve, reject) => {
        resolveReady = resolve;
        rejectReady = reject;
    });

    const fail = (message: string) => {
        handle.disposed = true;
        if (registry.get(rows)?.worker === worker) registry.delete(rows);
        worker.terminate();
        rejectReady(new Error(message));
        // A failure after initialization must also settle queued requests so
        // the UI can show Retry, instead of waiting for a dead worker forever.
        for (const [id, request] of pending) {
            if (request.worker === worker) {
                pending.delete(id);
                request.resolve({ type: 'result', id, move: -1, error: message });
            }
        }
    };

    worker.onmessage = (event: MessageEvent<WorkerMessage>) => {
        const msg = event.data;
        if (msg.type === "ready") {
            if (msg.error) {
                fail(msg.error);
            } else {
                resolveReady();
            }
            return;
        }
        if (msg.type === "result") {
            const request = pending.get(msg.id);
            if (request?.worker === worker) {
                pending.delete(msg.id);
                request.resolve(msg);
            }
        }
    };
    worker.onerror = (event) => fail(event.message || "The opponent stopped unexpectedly. Please try again.");
    worker.onmessageerror = () => fail("The opponent sent an unreadable response. Please try again.");

    const handle: SolverHandle = { worker, ready, disposed: false, dispose: () => fail('Review cancelled') };
    registry.set(rows, handle);
    worker.postMessage({ type: "init", baseUrl: base, rows, review: registry !== handles });
    return handle;
}

/** Eagerly create and initialize the solver for the given board. Resolves once ready. */
export function getSolverReady(board: Board): Promise<void> {
    return getHandle(board.rows).ready;
}

export function getSolver6BookDepth(): Promise<BookDepth | null> {
    if (!solver6BookDepth) {
        solver6BookDepth = fetch(`${base}books/solver6/v3/manifest.json`)
            .then(async response => {
                if (!response.ok) {
                    return null;
                }
                const manifest = await response.json() as { minPly?: unknown; maxPly?: unknown; completeMaxPly?: unknown };
                const minPly = manifest.minPly;
                const maxPly = manifest.maxPly;
                if (!Number.isInteger(minPly) || !Number.isInteger(maxPly)) {
                    return null;
                }
                const completeMaxPly = manifest.completeMaxPly;
                if (!Number.isInteger(completeMaxPly)) {
                    return null;
                }
                return { minPly: minPly as number, maxPly: maxPly as number, completeMaxPly: completeMaxPly as number };
            })
            .catch(() => null);
    }
    return solver6BookDepth ?? Promise.resolve(null);
}

/** Flattened cell index for a triangular board: row*(row+1)/2 + col. */
function indexFromRowCol(row: number, col: number): number {
    return row * (row + 1) / 2 + col;
}

function rowColFromIndex(idx: number): [number, number] {
    const row = Math.floor((Math.sqrt(8 * idx + 1) - 1) / 2);
    const col = idx - row * (row + 1) / 2;
    return [row, col];
}

/** Flatten a board into the triangular cell array the solver expects. */
function cellsOf(board: Board): number[] {
    const cells = new Array<number>(board.cellCount).fill(0);
    for (let row = 0; row < board.rows; row++) {
        for (let col = 0; col <= row; col++) {
            cells[indexFromRowCol(row, col)] = board.at(row, col);
        }
    }
    return cells;
}

/** Send one request to the board's worker and await its result. */
async function request(
    board: Board,
    type: "solve" | "mcts" | "book" | "evaluate" | "bookEvaluate",
    extra?: { limits?: SolverLimits; opts?: MctsOptions },
    registry = handles,
): Promise<ResultMessage> {
    const handle = getHandle(board.rows, registry);
    await handle.ready;
    if (handle.disposed) throw new Error('Review cancelled');
    const cells = cellsOf(board);
    const id = nextRequestId++;
    const result = await new Promise<ResultMessage>((resolve) => {
        pending.set(id, { worker: handle.worker, resolve });
        try {
            handle.worker.postMessage({ type, id, cells, limits: extra?.limits, opts: extra?.opts });
        } catch (error) {
            pending.delete(id);
            resolve({type: 'result', id, move: -1, error: error instanceof Error ? error.message : 'Could not contact the opponent.'});
        }
    });
    if (result.error) {
        throw new Error(result.error);
    }
    return result;
}

/** An isolated, disposable solver: review searches never queue ahead of gameplay. */
export function createReviewSolver() {
    const registry = new Map<number, SolverHandle>();
    let disposed = false;
    const evaluate = async (board: Board, bookOnly: boolean) => {
        if (disposed) throw new Error('Review cancelled');
        const result = await request(board, bookOnly ? 'bookEvaluate' : 'evaluate', undefined, registry);
        if (bookOnly && (!result.fromBook || !result.moves)) return null;
        return (result.moves ?? []).map(({ move, score }) => {
            const [row, col] = rowColFromIndex(move);
            return { row, col, score };
        });
    };
    return {
        book: (board: Board) => evaluate(board, true),
        evaluate: async (board: Board) => (await evaluate(board, false)) ?? [],
        dispose() {
            disposed = true;
            for (const handle of registry.values()) handle.dispose();
            registry.clear();
        },
    };
}

/**
 * Asks the MCTS+solver opponent for a move. Unlike getSolverMoveResult this
 * never consults the opening book: it searches the opening and lets the exact
 * solver take over near the endgame. `source` is "mcts" unless the search proved
 * the position exactly (then "minmax").
 */
export async function getMctsMoveResult(board: Board, opts?: MctsOptions): Promise<SolverMoveResult | null> {
    const result = await request(board, "mcts", { opts });
    if (result.move < 0) {
        return null;
    }
    return {
        move: rowColFromIndex(result.move),
        source: result.proven ? "minmax" : "mcts",
        score: result.score,
    };
}

/**
 * Asks the solver for the best move on the current board.
 * @returns the chosen cell as [row, col], or null if no move is available.
 */
export async function getSolverMoveResult(board: Board, limits?: SolverLimits): Promise<SolverMoveResult | null> {
    const result = await request(board, "solve", { limits });
    if (result.move < 0) {
        return null;
    }
    return {
        move: rowColFromIndex(result.move),
        source: result.fromBook ? "book" : "minmax",
        score: result.score,
        limitAborted: result.limitAborted,
        earlyCutoff: result.earlyCutoff,
    };
}

export async function getSolverMove(board: Board): Promise<[number, number] | null> {
    return (await getSolverMoveResult(board))?.move ?? null;
}

/**
 * Looks up the current board in the loaded opening book only. Returns null on
 * book miss, missing shard, or any non-book result; it never falls back to live
 * exact solving.
 */
export async function getBookMoveResult(board: Board): Promise<SolverMoveResult | null> {
    const result = await request(board, "book");
    if (!result.fromBook || result.move < 0) {
        return null;
    }
    return {
        move: rowColFromIndex(result.move),
        source: "book",
        score: result.score,
    };
}

export async function getBookMove(board: Board): Promise<[number, number] | null> {
    return (await getBookMoveResult(board))?.move ?? null;
}

/**
 * Evaluates every legal move on the current board. Returns each empty cell with
 * the perfect-play final score of playing there (P1-positive: positive favors
 * player 1, negative favors player 2, 0 is a draw).
 */
export async function getSolverMoveScores(
    board: Board,
): Promise<{ row: number; col: number; score: number }[]> {
    const result = await request(board, "evaluate");
    return (result.moves ?? []).map(({ move, score }) => {
        const [row, col] = rowColFromIndex(move);
        return { row, col, score };
    });
}

/**
 * Exact perfect-play value (P1-positive) of a single position — the child board
 * reached after one move. Reads the book first, else solves live; returns the
 * score for terminal positions too. Used to evaluate hint cells one at a time so
 * the overlay can fill in incrementally instead of blocking on a single
 * all-moves solve.
 */
export async function getPositionScore(board: Board): Promise<number | null> {
    const result = await request(board, "solve");
    return typeof result.score === "number" ? result.score : null;
}

/**
 * Evaluates every legal move using only the opening book — no live solving.
 * Each book entry stores the perfect-play score of every legal move at that
 * position, so this is a single book lookup of the current position (vs. a full
 * solve per move for the live evaluator). It lets the hint overlay turn on as
 * early as the book reaches, rather than only near the endgame.
 *
 * Returns one entry per legal move (P1-positive scores), or null on a book miss
 * (position outside the book's ply range / uncovered) so the caller can fall
 * back to the live evaluator.
 */
export async function getBookMoveScores(
    board: Board,
): Promise<{ row: number; col: number; score: number }[] | null> {
    const result = await request(board, "bookEvaluate");
    if (!result.fromBook || !result.moves) {
        return null;
    }
    return result.moves.map(({ move, score }) => {
        const [row, col] = rowColFromIndex(move);
        return { row, col, score };
    });
}
